/**
 * « AUTORISER LC PORTE » — modèle pur de la page /porte/connexion (testable
 * sans navigateur). Le serveur décide (droits, confirmation d'identité, code) ;
 * ici on lit la demande, on dit les choses en français, et on vérifie que
 * l'adresse de retour est bien celle de l'app LC Porte avant d'y aller.
 */

/** Le schéma de retour de l'app : une constante (le serveur construit l'adresse, la page la revérifie). */
export const LCPORTE_RETOUR = 'lcporte://connexion'
/** L'identifiant de la demande, gardé le temps du parcours de connexion (`return_to` perd la requête). */
export const CLE_DEMANDE = 'gestion.porte-demande'

const UUID_RX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

interface Stockage { getItem(k: string): string | null; setItem(k: string, v: string): void }

/** L'identifiant de la demande : celui de l'adresse (gardé de côté), sinon celui gardé avant la connexion. */
export function lireDemandeId(search: string, stockage: Stockage | null): string | null {
  const brut = new URLSearchParams(search).get('demande')
  if (brut && UUID_RX.test(brut)) {
    const id = brut.toLowerCase()
    try { stockage?.setItem(CLE_DEMANDE, id) } catch { /* stockage indisponible */ }
    return id
  }
  if (brut) return null
  try {
    const garde = stockage?.getItem(CLE_DEMANDE) ?? null
    return garde && UUID_RX.test(garde) ? garde.toLowerCase() : null
  } catch { return null }
}

/** La page de connexion de Gestion, avec retour ici. */
export function retourConnexion(id: string): string {
  return `/login?return_to=${encodeURIComponent(`/porte/connexion?demande=${id}`)}`
}

/** L'adresse de retour vers l'app, revérifiée : `lcporte://connexion?demande=<id>&(code=…|refus=1)`, rien d'autre. */
export function retourSur(u: unknown, id: string): string | null {
  if (typeof u !== 'string' || !u.startsWith(`${LCPORTE_RETOUR}?`)) return null
  const q = new URLSearchParams(u.slice(LCPORTE_RETOUR.length + 1))
  if (q.get('demande') !== id) return null
  const code = q.get('code')
  if (code !== null && !/^[A-Za-z0-9_-]{43}$/.test(code)) return null
  if (code === null && q.get('refus') !== '1') return null
  return u
}

/** Le retour « refusé » vers l'app, construit ici (quand Gestion ne répond plus). */
export function retourRefus(id: string): string {
  return `${LCPORTE_RETOUR}?${new URLSearchParams({ demande: id, refus: '1' }).toString()}`
}

export interface DroitPorte { cle: string; libelle: string }

export interface VueDemande {
  demande: {
    demande_id: string
    mode: 'POSTE' | 'INSCRIPTION'
    etat: 'OUVERTE' | 'GARANTIE' | 'FINIE' | 'EXPIREE'
    cree_a: string
    expire_a: string
    telephone: { nom: string; proprietaire: 'CLUB' | 'PERSONNEL'; plateforme: string | null; revoque: boolean }
    adresse_ouverture: string | null
  }
  moi: { nom: string; droits: DroitPorte[] }
  authentification: { forte_a: string | null; second_facteur: boolean; confirmation_requise: boolean }
  peut_autoriser: boolean
  raison: string | null
  /** Téléphone du club et rien à autoriser : Gestion a déjà fermé la session sur ce téléphone. */
  deconnecte?: boolean
}

/** La réponse a-t-elle la forme attendue ? (`apiRead` rend aussi les corps d'erreur.) */
export function estVueDemande(v: unknown): v is VueDemande {
  if (typeof v !== 'object' || v === null) return false
  const o = v as Partial<VueDemande>
  return !!o.demande && typeof o.demande.demande_id === 'string' && (o.demande.mode === 'POSTE' || o.demande.mode === 'INSCRIPTION')
    && !!o.demande.telephone && typeof o.demande.telephone.nom === 'string'
    && !!o.moi && Array.isArray(o.moi.droits) && typeof o.peut_autoriser === 'boolean'
}

/** La question posée, et sa conséquence. */
export function phraseAction(v: VueDemande): { question: string; consequence: string } {
  if (v.demande.mode === 'INSCRIPTION') {
    return {
      question: 'Inscrire votre téléphone personnel ?',
      consequence: 'Ce téléphone pourra ensuite prendre votre poste à la porte avec Face ID ou votre empreinte, avec ces droits :',
    }
  }
  return {
    question: 'Prendre votre poste sur ce téléphone du club ?',
    consequence: 'Vous y travaillerez à votre nom jusqu’à la fin de la nuit, avec ces droits :',
  }
}

const PLATEFORMES: Record<string, string> = { ios: 'iPhone', android: 'Android', web: 'Navigateur' }
export function libellePlateforme(p: string | null): string | null {
  return p ? PLATEFORMES[p] ?? null : null
}

/** Réseau du club (adresse privée) ou Internet ? Rien d'autre n'est déduit d'une adresse. */
export function origineAdresse(ip: string | null): 'club' | 'internet' | null {
  if (!ip) return null
  const v4 = /^(?:::ffff:)?(\d+)\.(\d+)\.(\d+)\.(\d+)$/i.exec(ip)
  if (v4) {
    const a = Number(v4[1]), b = Number(v4[2])
    return a === 10 || a === 127 || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168) ? 'club' : 'internet'
  }
  const v6 = ip.toLowerCase()
  return v6 === '::1' || /^f[cd]/.test(v6) ? 'club' : 'internet'
}

const HEURE = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit' })
export function heureParis(iso: string | null): string {
  if (!iso) return '—'
  const t = Date.parse(iso)
  return Number.isNaN(t) ? '—' : HEURE.format(t)
}

/** Le message d'une réponse en erreur, dit à la personne qui tient le téléphone. */
export function messageErreur(status: number, data: unknown): string {
  const o = typeof data === 'object' && data !== null ? data as { error?: unknown; message?: unknown } : {}
  if (o.error === 'STEPUP_REQUIRED') return 'Confirmation d’identité annulée : rien n’a été autorisé. Recommencez quand vous voulez.'
  if (typeof o.message === 'string' && o.message.trim()) return o.message
  if (status === 0) return 'Gestion n’a pas répondu. Vérifiez le réseau, puis réessayez.'
  if (status >= 500) return 'La billetterie ne répond pas pour le moment. Prenez votre poste avec le QR, ou réessayez dans un instant.'
  return 'Cette demande n’a pas pu être lue. Recommencez depuis l’app LC Porte.'
}
